import React, {useState, useRef, useCallback} from 'react';
import {Button, Icon} from '@gravity-ui/uikit';
import {ArrowUpFromLine} from '@gravity-ui/icons';
import {useToaster} from '@gravity-ui/uikit';
import {UniversalImageUploadModal} from '../UniversalImageUploadModal/UniversalImageUploadModal';
import {useNotifications} from '../../lib';

interface StoryPreviewImageUploadButtonProps {
  onUploadSuccess?: () => void;
  aspectRatio?: number;
  className?: string;
  onUploadComplete: (uploadId: string) => Promise<void>; // Колбэк для вызова API
  resourceName?: string; // Название ресурса для уведомлений
  prepareUploadApi: (fileTypes: string[]) => Promise<{items: {id: string; url: string}[]}>; // API для подготовки загрузки
}

export const StoryPreviewImageUploadButton = ({
  onUploadSuccess,
  aspectRatio = 1 / 1,
  className,
  onUploadComplete,
  resourceName = 'Превью изображение',
  prepareUploadApi,
}: StoryPreviewImageUploadButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {add} = useToaster();
  const {createNotification} = useNotifications();

  const uploadFileDirectly = async (file: File, uploadUrl: string): Promise<void> => {
    const buffer = await file.arrayBuffer();
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: buffer,
    });

    if (!response.ok) {
      let errorBody = 'Unknown error during direct upload.';
      try {
        errorBody = await response.text();
      } catch (e) {
        // Ignore error reading body
      }
      throw new Error(`Direct upload failed with status ${response.status}: ${errorBody}`);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!file.type.startsWith('image/')) {
        add({
          name: 'image-upload-error',
          title: 'Ошибка загрузки изображения',
          content: 'Пожалуйста, выберите файл изображения.',
          theme: 'danger',
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setSelectedFile(file);
        setPreviewUrl(previewUrl);
        setIsModalOpen(true);
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    }
  };

  const handleUploadSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
  }, []);

  const handleConfirmCrop = async (blob: Blob) => {
    try {
      setIsPreparing(true);
      const file = new File([blob], 'preview.jpg', {type: 'image/jpeg'});

      // Подготавливаем загрузку
      const response = await prepareUploadApi([file.type]);
      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Prepare upload response is invalid or missing uploadUrl');
      }

      const uploadItem = response.items[0];

      // Загружаем файл
      setIsUploading(true);
      await uploadFileDirectly(file, uploadItem.url);

      // Вызываем API для обновления превью
      await onUploadComplete(uploadItem.id);

      createNotification({
        title: `${resourceName} успешно обновлено`,
        name: 'updateStoryPreviewImage',
        theme: 'success',
      });

      if (onUploadSuccess) {
        onUploadSuccess();
      }

      handleUploadSuccess();
    } catch (error: any) {
      console.error('Error during upload:', error);
      createNotification({
        title: `Ошибка при обновлении ${resourceName.toLowerCase()}`,
        name: 'updateStoryPreviewImageError',
        theme: 'danger',
      });
    } finally {
      setIsPreparing(false);
      setIsUploading(false);
    }
  };

  return (
    <>
      <Button view="raised" size="m" onClick={handleButtonClick} className={className}>
        <Icon data={ArrowUpFromLine} size={16} />
      </Button>

      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{display: 'none'}} />

      <UniversalImageUploadModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        file={selectedFile}
        previewUrl={previewUrl}
        onConfirm={handleConfirmCrop}
        onCancel={handleUploadSuccess}
        aspectRatio={aspectRatio}
        loading={isPreparing || isUploading}
      />
    </>
  );
};
